/*
 * Hero carousel — auto-advancing, opacity cross-fade, viewport height.
 *
 * Two things here are load-bearing and should not be "simplified" later:
 *
 * 1. Auto-advance stops permanently on the first user interaction, and no
 *    setting can switch that off. It is the mechanism that satisfies
 *    WCAG 2.2.2 (Pause, Stop, Hide) at Level A. The `autoplay` checkbox is a
 *    merchant control and does not satisfy 2.2.2, because the criterion
 *    protects the visitor rather than the shopkeeper. `show_pause_button`
 *    governs only whether the control is drawn on screen.
 *
 * 2. The header's colour is NOT set from here. This element publishes the
 *    active slide's tone on `data-active-tone` and fires `hero-carousel:tone`;
 *    assets/header-scheme-switch.js remains the single writer of
 *    `.header-wrapper.classList`. Two writers would fight over that element.
 */
class HeroCarousel extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.root = this.querySelector('[data-hero-carousel]');
    if (!this.root) return;

    this.slides = Array.from(this.root.querySelectorAll('[data-hero-carousel-slide]'));
    if (this.slides.length === 0) return;

    this.pauseButton = this.root.querySelector('[data-hero-carousel-pause]');
    this.index = 0;
    this.timer = null;
    // Deliberately not reset: connectedCallback runs again when the element is
    // moved in the DOM, and a visitor's stop has to outlive that.
    if (this.stopped === undefined) this.stopped = false;

    this.autoplayEnabled = this.root.dataset.autoplay === 'true';
    const speed = parseFloat(this.root.dataset.autoplaySpeed);
    this.delay = (Number.isFinite(speed) ? speed : 3.5) * 1000;
    const fade = parseInt(this.root.dataset.transitionSpeed, 10);
    this.fade = Number.isFinite(fade) ? fade : 250;

    // Published for the header's transition, so the recolour and the dissolve
    // run at the same duration. The rule that consumes it lives in the
    // header's own stylesheet, never in this section's.
    document.documentElement.style.setProperty('--hero-carousel-fade', `${this.fade}ms`);
    this.root.style.setProperty('--hero-carousel-fade', `${this.fade}ms`);

    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onMotionPreferenceChange = this.onMotionPreferenceChange.bind(this);
    this.onFirstInteraction = this.onFirstInteraction.bind(this);
    this.onPauseClick = this.onPauseClick.bind(this);

    if (this.reducedMotionQuery.addEventListener) {
      this.reducedMotionQuery.addEventListener('change', this.onMotionPreferenceChange);
    }

    if (this.pauseButton) {
      this.pauseButton.addEventListener('click', this.onPauseClick);
    }

    this.publishTone();

    if (this.canAutoplay()) {
      this.bindFirstInteraction();
      this.play();
    } else {
      // Nothing is moving — under reduced motion, with autoplay off, or with a
      // single slide — so there is nothing for the control to stop.
      this.retireControl();
    }
  }

  disconnectedCallback() {
    this.pause();
    this.unbindFirstInteraction();
    if (this.reducedMotionQuery && this.reducedMotionQuery.removeEventListener) {
      this.reducedMotionQuery.removeEventListener('change', this.onMotionPreferenceChange);
    }
    this.initialised = false;
  }

  canAutoplay() {
    return (
      this.autoplayEnabled &&
      this.slides.length > 1 &&
      !this.stopped &&
      !this.reducedMotionQuery.matches
    );
  }

  play() {
    if (this.timer) return;
    this.timer = window.setInterval(() => this.next(), this.delay);
  }

  pause() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  /*
   * Permanent. Nothing restarts auto-advance after this, which is the whole
   * point: a visitor who stops the motion does not get it back at the next
   * interval, and no combination of settings reaches this method to undo it.
   */
  stop() {
    this.stopped = true;
    this.pause();
    this.unbindFirstInteraction();
    this.retireControl();
  }

  next() {
    this.goTo((this.index + 1) % this.slides.length);
  }

  goTo(nextIndex) {
    if (nextIndex === this.index) return;
    const current = this.slides[this.index];
    const next = this.slides[nextIndex];
    if (!next) return;

    current.classList.remove('hero-carousel__slide--active');
    current.setAttribute('aria-hidden', 'true');
    next.classList.add('hero-carousel__slide--active');
    next.removeAttribute('aria-hidden');

    this.index = nextIndex;
    this.publishTone();
  }

  /*
   * The carousel states its tone; the header scheme switcher reads it. Keeping
   * the header mutation in one file is what stops the two mechanisms fighting.
   */
  publishTone() {
    const tone = this.slides[this.index].dataset.tone === 'light' ? 'light' : 'dark';
    if (this.root.dataset.activeTone === tone) return;
    this.root.dataset.activeTone = tone;
    document.dispatchEvent(new CustomEvent('hero-carousel:tone', { detail: { tone } }));
  }

  bindFirstInteraction() {
    if (this.interactionBound) return;
    this.interactionBound = true;
    // Scroll is deliberately excluded: on a full-bleed hero it would stop the
    // carousel almost immediately for every visitor.
    document.addEventListener('pointerdown', this.onFirstInteraction, { once: true });
    document.addEventListener('keydown', this.onFirstInteraction, { once: true });
    document.addEventListener('touchstart', this.onFirstInteraction, { once: true, passive: true });
  }

  unbindFirstInteraction() {
    if (!this.interactionBound) return;
    this.interactionBound = false;
    document.removeEventListener('pointerdown', this.onFirstInteraction);
    document.removeEventListener('keydown', this.onFirstInteraction);
    document.removeEventListener('touchstart', this.onFirstInteraction);
  }

  onFirstInteraction() {
    this.stop();
  }

  onPauseClick() {
    this.stop();
  }

  onMotionPreferenceChange(event) {
    if (event.matches) this.pause();
  }

  /*
   * The control's only job is to stop the motion, and stopping is permanent, so
   * there is no second state for it to be in. Presenting it as a pause/play
   * toggle left `aria-pressed="true"` on a button that could never be
   * un-pressed. Once the motion is gone the control is removed from the page
   * and from the accessibility tree with it.
   */
  retireControl() {
    if (!this.pauseButton) return;
    this.pauseButton.hidden = true;
  }
}

if (!customElements.get('hero-carousel')) {
  customElements.define('hero-carousel', HeroCarousel);
}
